import i18next from 'i18next';

import { fieldAttributeOptions } from 'config/constants';

/**
 * Attributes a field may be shown under.
 *
 * Metadata fields carry no attribute of their own, and record list views expose
 * values only, so both collapse to the first option -- the rule the pre-rewrite
 * picker applied in SettingsFields.buildField.
 */
export function allowedAttributes(field, isRecordListView) {
  if ((field && field.meta) || isRecordListView) {
    return fieldAttributeOptions.slice(0, 1);
  }
  return fieldAttributeOptions.slice();
}

/**
 * Attributes already taken by selected columns of one field.
 *
 * Columns built from the tree carry `suuid`; columns adopted from a saved view
 * carry `fieldSUUID`, so both identify the field. `excludeCuuid` drops the
 * column being edited, leaving only its siblings.
 */
export function usedAttributes(fields, suuid, excludeCuuid) {
  var used = [];
  if (!fields || !suuid) return used;
  for (var i = 0; i < fields.length; i++) {
    var column = fields[i];
    if (excludeCuuid && column.cuuid === excludeCuuid) continue;
    if (column.suuid !== suuid && column.fieldSUUID !== suuid) continue;
    var attribute = column.fieldAttribute || fieldAttributeOptions[0];
    if (used.indexOf(attribute) === -1) used.push(attribute);
  }
  return used;
}

/**
 * The attribute a new column of this field should take, or null when the field
 * already supplies a column for every attribute it allows.
 */
export function firstFreeAttribute(field, fields, isRecordListView) {
  if (!field) return null;
  var allowed = allowedAttributes(field, isRecordListView);
  var used = usedAttributes(fields, field.suuid || field.fieldSUUID);
  for (var i = 0; i < allowed.length; i++) {
    if (used.indexOf(allowed[i]) === -1) return allowed[i];
  }
  return null;
}

/** True when the field has no attribute left to add. Drives the tree's disabled state. */
export function isFieldFullyUsed(field, fields, isRecordListView) {
  return firstFreeAttribute(field, fields, isRecordListView) === null;
}

/** True when the column holds the field's value rather than its user or timestamp. */
export function isValueAttribute(column) {
  if (!column) return false;
  var attribute = column.fieldAttribute || fieldAttributeOptions[0];
  return attribute === fieldAttributeOptions[0];
}

/**
 * Label for an attribute in the picker.
 *
 * Deliberately not formatColumnAttribute/formatAttributeLabel from the table
 * header module: those blank `value`, because the rendered header hides it. A
 * control has to read in every state, so all three tokens are translated here.
 * Both resolve the same i18n keys.
 */
export function attributeOptionLabel(attribute, t) {
  if (!attribute) return '';
  if (fieldAttributeOptions.indexOf(attribute) === -1) return '';
  var translate = t || i18next.t.bind(i18next);
  return translate('settings:fieldAttribute_' + attribute);
}
